<template>
  <v-dialog :value.sync="value" fullscreen content-class="cropper-dialog" @click:outside="$emit('input', false)" @input="$emit('input', false)">
    <div id="cropperInner">
      <cropper
        ref="cropper"
        class="cropper"
        :src="image.url"
        @ready="ready"
        :transitions="true"
        @change="updateSize"
        :stencil-props="{
          previewClass: 'preview'
        }"
        :default-position="{
          left: 0,
          top: 0
        }"
        :default-size="defaultSize"
      />
      <div id="cropperHorizontalBase">
        <v-btn color="success" class="mr-3" @click="cropImageDialog = true">
          <v-icon class="mr-2">mdi-content-save</v-icon>
          {{ $t("PANEL.MEDIA.CROPPER.SAVE_BTN") }}
        </v-btn>
        <v-btn icon class="white--text" large @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <div id="cropperVerticalBase" class="left">
        <div class="cropper-actions-title">
          <v-chip label dark color="deep-purple" class="px-2 mb-5">
            <b>{{ $t("PANEL.MEDIA.CROPPER.SHAPE_TEXT") }}</b>
          </v-chip>
        </div>
        <v-tooltip right color="white">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" fab small class="mb-5" @click="flip(true, false)">
              <v-icon size="20">mdi-flip-horizontal</v-icon>
            </v-btn>
          </template>
          <span class="black--text">{{ $t("PANEL.MEDIA.CROPPER.FLIP_HORIZONTAL") }}</span>
        </v-tooltip>
        <v-tooltip right color="white">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" fab small class="mb-5" @click="flip(false, true)">
              <v-icon size="20">mdi-flip-vertical</v-icon>
            </v-btn>
          </template>
          <span class="black--text">{{ $t("PANEL.MEDIA.CROPPER.FLIP_VERTICAL") }}</span>
        </v-tooltip>
        <v-tooltip right color="white">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" fab small class="mb-5" @click="rotate(90)">
              <v-icon size="20">mdi-rotate-right</v-icon>
            </v-btn>
          </template>
          <span class="black--text">{{ $t("PANEL.MEDIA.CROPPER.TURN_RIGHT") }}</span>
        </v-tooltip>
        <v-tooltip right color="white">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" fab small class="mb-5" @click="rotate(-90)">
              <v-icon size="20">mdi-rotate-left</v-icon>
            </v-btn>
          </template>
          <span class="black--text">{{ $t("PANEL.MEDIA.CROPPER.TURN_LEFT") }}</span>
        </v-tooltip>
      </div>
      <div id="cropperVerticalBase" class="right">
        <div class="cropper-actions-title">
          <v-chip label dark color="deep-purple" class="mb-5">
            <b>{{ $t("PANEL.MEDIA.CROPPER.CROP_TEXT") }}</b>
          </v-chip>
        </div>
        <v-tooltip left color="white">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" fab small class="mb-5" @click="resize(2, 2)">
              <v-icon size="20">mdi-arrow-expand-all</v-icon>
            </v-btn>
          </template>
          <span class="black--text">{{ $t("PANEL.MEDIA.CROPPER.EXTEND", { INFO: "(x2)" }) }}</span>
        </v-tooltip>
        <v-tooltip left color="white">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" fab small class="mb-5" @click="resize(1, 2)">
              <v-icon size="22">mdi-pan-vertical</v-icon>
            </v-btn>
          </template>
          <span class="black--text">{{ $t("PANEL.MEDIA.CROPPER.EXTEND_VERTICAL", { INFO: "(x2)" }) }}</span>
        </v-tooltip>
        <v-tooltip left color="white">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" fab small class="mb-5" @click="resize(2, 1)">
              <v-icon size="22">mdi-pan-horizontal</v-icon>
            </v-btn>
          </template>
          <span class="black--text">{{ $t("PANEL.MEDIA.CROPPER.EXTEND_HORIZONTAL", { INFO: "(x2)" }) }}</span>
        </v-tooltip>
        <v-tooltip left color="white">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" fab small class="mb-5" @click="resize(0.5, 0.5)">
              <v-icon size="20">mdi-arrow-collapse-all</v-icon>
            </v-btn>
          </template>
          <span class="black--text">{{ $t("PANEL.MEDIA.CROPPER.REDUCE", { INFO: "(x1/2)" }) }} </span>
        </v-tooltip>
        <v-tooltip left color="white">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" fab small class="mb-5" @click="maximize()">
              <v-icon size="23">mdi-fullscreen</v-icon>
            </v-btn>
          </template>
          <span class="black--text">{{ $t("PANEL.MEDIA.CROPPER.SELECT_ALL_AREA") }}</span>
        </v-tooltip>
        <v-tooltip left color="white">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" fab small class="mb-5" @click="center()">
              <v-icon size="23">mdi-pan</v-icon>
            </v-btn>
          </template>
          <span class="black--text">{{ $t("PANEL.MEDIA.CROPPER.ALIGN_CENTER") }}</span>
        </v-tooltip>
      </div>
    </div>
    <v-dialog v-model="cropImageDialog" max-width="600">
      <v-card>
        <v-card-title>
          {{ $t("PANEL.MEDIA.CROPPER.CHOOSE_ACTION") }}
          <v-spacer></v-spacer>
          <v-btn icon small @click="cropImageDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-btn color="deep-purple" class="mr-5 text-capitalize" dark @click="uploadNewImage">
            <v-icon left>mdi-content-save-edit</v-icon>
            {{ $t("PANEL.MEDIA.CROPPER.OVERWRITE_BTN") }}
          </v-btn>
          <v-btn color="deep-purple" class="text-capitalize" dark @click="uploadCroppedImage">
            <v-icon left>mdi-content-save-all</v-icon>
            {{ $t("PANEL.MEDIA.CROPPER.PREVERSE_BTN") }}
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import MediaService from "@/services/MediaService";

/**
 * image-restriction="stencil"
 * özelliği ile viewport alanında uzaklaştır
 */
export default {
  name: "ImageCropper",
  components: {
    Cropper
  },
  props: {
    value: {
      required: true,
      default: false
    },
    image: {
      required: true
    }
  },
  data: () => ({
    readyState: false,
    size: {
      width: null,
      height: null
    },
    cropImageDialog: false
  }),
  methods: {
    updateSize({ coordinates }) {
      this.size.width = Math.round(coordinates.width);
      this.size.height = Math.round(coordinates.height);
    },
    ready() {
      this.readyState = true;
    },
    defaultSize({ imageSize, visibleArea }) {
      return {
        width: (visibleArea || imageSize).width,
        height: (visibleArea || imageSize).height
      };
    },
    flip(x, y) {
      this.$refs.cropper.flip(x, y);
    },
    rotate(angle) {
      this.$refs.cropper.rotate(angle);
    },
    // download() {
    //   const result = this.$refs.cropper.getResult().canvas.toDataURL();
    //   const newTab = window.open();
    //   newTab.document.body.innerHTML = `<img src="${result}"></img>`;
    // },
    change(args) {
      args;
    },
    resize(width = 1, height = 1) {
      let startCoordinates;
      this.$refs.cropper.setCoordinates([
        ({ coordinates }) => {
          startCoordinates = coordinates;
          return {
            width: coordinates.width * width,
            height: coordinates.height * height
          };
        },
        ({ coordinates }) => ({
          left: startCoordinates.left + (startCoordinates.width - coordinates.width) / 2,
          top: startCoordinates.top + (startCoordinates.height - coordinates.height) / 2
        })
      ]);
    },
    center() {
      this.$refs.cropper.setCoordinates(({ coordinates, imageSize }) => ({
        left: imageSize.width / 2 - coordinates.width / 2,
        top: imageSize.height / 2 - coordinates.height / 2
      }));
    },
    maximize() {
      const { cropper } = this.$refs;
      const center = {
        left: cropper.coordinates.left + cropper.coordinates.width / 2,
        top: cropper.coordinates.top + cropper.coordinates.height / 2
      };
      cropper.setCoordinates([
        ({ imageSize }) => ({
          width: imageSize.width,
          height: imageSize.height
        }),
        ({ coordinates }) => ({
          left: center.left - coordinates.width / 2,
          top: center.top - coordinates.height / 2
        })
      ]);
    },
    croppedFileName() {
      return this.image.name.replace("." + this.image.extension, "") + "-cropped." + this.image.extension;
    },
    uploadNewImage() {
      const { canvas } = this.$refs.cropper.getResult(),
        fileName = this.image.name;

      if (canvas) {
        canvas.toBlob(blob => {
          const file = new File([blob], this.image.name, {
            type: this.image.type
          });

          MediaService.renew({ file, fileName })
            .then(res => {
              let response = res.data;
              if (response.status === 200) {
                this.$snotify.success(this.$t("PANEL.MEDIA.CROPPER.IMAGE_EDIT_UPLOAD_NEW_SUCCESSFUL"), this.$t("PANEL.NOTIFY.SUCCESS"));
                this.$emit("input", false);
              } else {
                this.$snotify.error(response.data.errMessage, this.$t("PANEL.NOTIFY.ERROR"));
              }
            })
            .then(() => {
              this.$emit("cropped", file);
            });
        }, "image/jpeg");
      }
    },
    uploadCroppedImage() {
      const { canvas } = this.$refs.cropper.getResult();
      if (canvas) {
        canvas.toBlob(blob => {
          const file = new File([blob], this.croppedFileName(), {
            type: this.image.type
          });

          MediaService.uploadImage([file])
            .then(res => {
              let response = res.data;
              if (response.status === 200) {
                this.$snotify.success(this.$t("PANEL.MEDIA.CROPPER.IMAGE_EDIT_OVERWRITE_SUCCESSFUL"), this.$t("PANEL.NOTIFY.SUCCESS"));
                this.$emit("input", false);
              } else {
                this.$snotify.error(response.data.errMessage, this.$t("PANEL.NOTIFY.ERROR"));
              }
            })
            .then(() => {
              this.$emit("cropped", file);
            });
          // You can use axios, superagent and other libraries instead here
          // fetch("http://example.com/upload/", {
          //   method: "POST",
          //   body: form
          // });
          // Perhaps you should add the setting appropriate file format here
        }, "image/jpeg");
      }
    }
  },
  watch: {
    value: {
      handler() {
        if (this.readyState) {
          this.$refs.cropper.reset();
          // this.maximize();
        }
      },
      immediate: true
    }
  }
};
</script>

<style scoped>
.cropper {
  height: 100vh;
  background: #ddd;
}

#cropperHorizontalBase {
  position: absolute;
  top: 0;
  right: 0;
  margin: 10px;
}

#cropperVerticalBase {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
}

#cropperVerticalBase.left {
  left: 10px;
}

#cropperVerticalBase.right {
  right: 10px;
}

#cropperInner {
  position: relative;
}

.cropper-actions-title {
  flex-shrink: 1;
}
</style>
<style>
.cropper-dialog.v-dialog {
  overflow: hidden;
}

/* .preview {
  border: 1px solid red;
} */
</style>
